<template>
  <section v-where-view class="sight-location">
    <h2 class="sight-location__title">{{ t('location.title') }}</h2>
    <div class="sight-location__meta">
      <p v-if="location.description" class="sight-location__address">
        <span class="sight-location__label">{{ t('location.addressLabel') }}&nbsp;</span>{{ location.description }}
      </p>
      <p v-if="coordinates" class="sight-location__coords">
        <span class="sight-location__label">{{ t('location.coordinatesLabel') }}&nbsp;</span>{{ coordinates }}
        <button
          type="button"
          class="sight-location__copy"
          :aria-label="t('location.copyCoordinates')"
          @click="copyCoordinates"
        >
          <IconBase name="copy" :size="20" color="--alias-color-text-subtle" />
        </button>
      </p>
    </div>
    <div v-if="isMapEnabled && location.latitude && location.longitude" class="sight-location__map">
      <SightMap :latitude="location.latitude" :longitude="location.longitude" :zoom="15" />
    </div>
    <UiToast :open="isCopiedToastOpen" :z-index="10000" @close="isCopiedToastOpen = false">
      <template #content>{{ t('location.coordinatesCopied') }}</template>
    </UiToast>
  </section>
</template>

<script setup lang="ts">
import { UiToast } from '@experience/core-toast-ui';
import { useI18n } from '@experience/i18n';
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';

import { useAnalytics } from '@/shared/lib/analytics';
import { IconBase } from '@/shared/ui/iconBase';
import SightMap from '@/shared/ui/SightMap.vue';

import ru from '../i18n/ru.json';

import type { AttractionLocation } from '../models/attractionCard';

const props = defineProps<{
  location: AttractionLocation;
}>();

const { useViewTracker } = useAnalytics();
const vWhereView = useViewTracker('sight:where.view');
const route = useRoute();

const isMapEnabled = computed(() => route.query.map !== 'false');

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const isCopiedToastOpen = ref(false);

const coordinates = computed(() => {
  const { latitude, longitude } = props.location;

  if (latitude == null || longitude == null) {
    return '';
  }

  return `${latitude}, ${longitude}`;
});

const copyCoordinates = async () => {
  if (!coordinates.value || !navigator.clipboard) {
    return;
  }

  try {
    await navigator.clipboard.writeText(coordinates.value);
    isCopiedToastOpen.value = false;
    await nextTick();
    isCopiedToastOpen.value = true;
  } catch {
    // копирование не должно ломать страницу
  }
};
</script>

<style scoped lang="scss">
.sight-location__title {
  margin: 0;

  @include typography.base-800-800-semi;

  @include tablet {
    @include typography.base-700-500-semi;
  }
}

.sight-location__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
  margin-top: 8px;

  @include mobile {
    flex-direction: column;
    gap: 0;
  }
}

.sight-location__address,
.sight-location__coords {
  margin: 0;

  @include typography.base-500-400-regular;

  @include tablet {
    @include typography.base-400-400-regular;
  }
}

.sight-location__label {
  color: var(--alias-color-text-subtle);
}

.sight-location__address {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  color: var(--alias-color-text-default);
}

.sight-location__coords {
  display: flex;
  flex-shrink: 0;
  gap: 4px;
  align-items: center;
  color: var(--alias-color-text-default);
}

.sight-location__copy {
  display: inline-flex;
  padding: 0;
  color: var(--alias-color-text-subtle);
  cursor: pointer;
  background: none;
  border: none;
}

.sight-location__map {
  width: 100%;
  height: 343px;
  margin-top: var(--alias-space-16, 16px);
  overflow: hidden;
  border-radius: var(--alias-radius-16, 16px);
}
</style>
